import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

const MIN_LENGTH = 8;

export type PasswordStrength = "empty" | "weak" | "fair" | "good" | "strong";

function computeStrength(password: string): PasswordStrength {
  if (!password) return "empty";
  let score = 0;
  if (password.length >= MIN_LENGTH) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  if (score <= 1) return "weak";
  if (score === 2) return "fair";
  if (score === 3) return "good";
  return "strong";
}

function strengthToProgress(strength: PasswordStrength): number {
  switch (strength) {
    case "empty":
      return 0;
    case "weak":
      return 25;
    case "fair":
      return 50;
    case "good":
      return 75;
    case "strong":
      return 100;
  }
}

function strengthToVariant(strength: PasswordStrength): string {
  switch (strength) {
    case "empty":
      return "bg-muted";
    case "weak":
      return "bg-destructive";
    case "fair":
      return "bg-amber-500";
    case "good":
      return "bg-lime-500";
    case "strong":
      return "bg-green-600";
  }
}

export interface PasswordStrengthIndicatorProps {
  password: string;
  showRules?: boolean;
  className?: string;
  rulesLabels?: {
    minLength: string;
    uppercase: string;
    number: string;
    special: string;
  };
  strengthLabels?: {
    weak: string;
    fair: string;
    good: string;
    strong: string;
  };
}

export function PasswordStrengthIndicator({
  password,
  showRules = true,
  className,
  rulesLabels,
  strengthLabels,
}: PasswordStrengthIndicatorProps): JSX.Element {
  const strength = useMemo(() => computeStrength(password), [password]);
  const progress = strengthToProgress(strength);

  const rules = useMemo(() => {
    if (!showRules) return null;
    const minLength = password.length >= MIN_LENGTH;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^a-zA-Z0-9]/.test(password);
    return {
      minLength,
      hasUppercase: hasUppercase && hasLowercase,
      hasNumber,
      hasSpecial,
    };
  }, [password, showRules]);

  const strengthLabel =
    strength !== "empty" && strengthLabels
      ? strengthLabels[strength]
      : strength !== "empty"
        ? strength
        : null;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2">
        <Progress
          value={progress}
          className="h-1.5 flex-1"
          indicatorClassName={strengthToVariant(strength)}
        />
        {strengthLabel ? (
          <span
            className={cn(
              "text-xs font-medium capitalize",
              strength === "weak" && "text-destructive",
              strength === "fair" && "text-amber-600",
              strength === "good" && "text-lime-600",
              strength === "strong" && "text-green-600"
            )}
          >
            {strengthLabel}
          </span>
        ) : null}
      </div>
      {showRules && rules && rulesLabels ? (
        <ul className="text-muted-foreground text-xs space-y-0.5">
          <li className={rules.minLength ? "text-green-600" : ""}>
            {rules.minLength ? "✓ " : "○ "}
            {rulesLabels.minLength}
          </li>
          <li className={rules.hasUppercase ? "text-green-600" : ""}>
            {rules.hasUppercase ? "✓ " : "○ "}
            {rulesLabels.uppercase}
          </li>
          <li className={rules.hasNumber ? "text-green-600" : ""}>
            {rules.hasNumber ? "✓ " : "○ "}
            {rulesLabels.number}
          </li>
          <li className={rules.hasSpecial ? "text-green-600" : ""}>
            {rules.hasSpecial ? "✓ " : "○ "}
            {rulesLabels.special}
          </li>
        </ul>
      ) : null}
    </div>
  );
}
